import { Col, Row } from "react-bootstrap";
import { CompetitionPickerListView } from "./CompetitionPickerViews";
import { Competition, CompetitionChoice } from "../../../models/interfaces/Competition";
import { useCallback } from "react";
import { useTimedLoad } from "../../../helpers/useTimedLoad";
import { LoadStatus } from "../../../components/containers/LoadStatus";
import { ExpandableCardContextProvider } from "../../../context/useExpandableCardContext";
import CompetitionLeadersDisplay from "../../competitions/modules/CompetitionLeadersDisplay";

interface Props {
    competitions: CompetitionChoice[];
    selectedCompetition: Competition;
    onSelectCompetition: (competition: CompetitionChoice) => void;
}

export function EventLeadersView({ competitions, selectedCompetition, onSelectCompetition }: Props) {
    const loadLeaders = useCallback(() => {
        return selectedCompetition.leaderboardProvider
            .once()
            .then((leaderGroups) => {
                const leaderGroupPromises = leaderGroups.map((leaderGroup) => {
                    const playerSummaryPromises = leaderGroup.players.map((playerSummary) => {
                        return leaderGroup
                            .displayTextForStatResolvers(playerSummary.statResolvers)
                            .then((playerText) => {
                                return {
                                    player: playerSummary.player,
                                    text: playerText,
                                };
                            });
                    });
                    return Promise.allSettled(playerSummaryPromises)
                        .then((playerSummaryResults) => {
                            return playerSummaryResults.flatMap((playerSummaryResult) => {
                                if (playerSummaryResult.status === 'fulfilled') {
                                    return [playerSummaryResult.value];
                                } else {
                                    return [];
                                }
                            });
                        })
                        .then((inflatedPlayerSummaries) => {
                            return {
                                title: leaderGroup.title,
                                sortOrder: leaderGroup.sortOrder,
                                players: inflatedPlayerSummaries,
                            };
                        });
                });
                return Promise.all(leaderGroupPromises);
            })
            .then((inflatedLeaderGroups) => {
                return inflatedLeaderGroups.sort((left, right) => left.sortOrder - right.sortOrder);
            });
    }, [selectedCompetition]);
    const leaders = useTimedLoad(loadLeaders, 'leaderboard');
    const competitionLeaders = leaders.data;



    return (
        <div style={{width: '100%', paddingTop: '10px'}}>
            <Row noGutters className="matchContainer" style={{gap: '10px', padding: '20px'}}>
            <Col xs={4}>
                <CompetitionPickerListView
                    competitions={competitions}
                    selectedCompetition={selectedCompetition}
                    onCompetitionSelect={onSelectCompetition}
                />
            </Col>
            <Col>
                <>
                    <LoadStatus {...leaders} label="leaderboard" />
                    {competitionLeaders && (
                        competitionLeaders.reduce((hasLeaders, currentValue) => {
                            if (hasLeaders) {
                                return true;
                            } else {
                                return currentValue.players.length > 0;
                            }
                        }, false) ? 
                            competitionLeaders.map((leaderType) => {
                                return (
                                    <ExpandableCardContextProvider
                                        key={leaderType.title}
                                        headerText={leaderType.title}
                                        buttonDisplayOptions={{
                                            expanded: `Collapse ${leaderType.title}`,
                                            collapsed: `Show All ${leaderType.title}`,
                                        }}
                                        showExpand={leaderType.players.length > 5}
                                    >
                                        <CompetitionLeadersDisplay leaderType={leaderType} />
                                    </ExpandableCardContextProvider>
                                );
                            })
                            : <>No individual statistics have been recorded for this division.</>
                    )}
                </>
            </Col>
            </Row>
        </div>
    );
}